import { isNull } from 'lodash/fp';
import React, { Dispatch, SetStateAction, useMemo } from 'react';

import styles from './NettingEngine.module.scss';
import { ISDAJurisdictionalNettingInformation, GMRAJurisdictionalNettingInformation } from './store';
import { CollateralisationMethodSummary, CloseOutNettingSummary } from '../store/types';
import { NettingEngineJurisdictionResultsSection } from './NettingEngineJurisdictionResultsSection';
import { AgreementType } from '../../admin/documents/store/types';

const { green, red, amber, yellowGreen } = styles;

interface NettingEngineJurisdictionSummaryProps {
    jurisdictionalResults: ISDAJurisdictionalNettingInformation | GMRAJurisdictionalNettingInformation;
    viewCustomConclusions: boolean;
    setTransactionConditionModalOpen: Dispatch<SetStateAction<boolean>>
    agreementType: AgreementType;
}

export const NettingEngineJurisdictionSummary: React.FC<NettingEngineJurisdictionSummaryProps> = ({ jurisdictionalResults, viewCustomConclusions, agreementType }) => {
    const { summary, clientSignOffDetails, clientSignOffSummary } = jurisdictionalResults;

    const customConclusionsExist = useMemo(() => !isNull(clientSignOffDetails) && viewCustomConclusions, [clientSignOffDetails, viewCustomConclusions]);
    const summaryValue = useMemo(() => customConclusionsExist ? clientSignOffSummary : summary, [customConclusionsExist, clientSignOffSummary, summary]);

    const collateralSummaryValue = useMemo(() => {
        if (agreementType === AgreementType.GMRA) {
            return customConclusionsExist ? (jurisdictionalResults as GMRAJurisdictionalNettingInformation).clientSignOffCollateralSummary : (jurisdictionalResults as GMRAJurisdictionalNettingInformation).collateralSummary;
        }
        return null;
    }, [customConclusionsExist, jurisdictionalResults, agreementType]);

    const closeOutSummaryColour = useMemo(() => {
        switch (summaryValue) {
            case CloseOutNettingSummary.YES:
                return green;
            case CloseOutNettingSummary.YES_PRODUCTS_UNKNOWN:
                return yellowGreen;
            case CloseOutNettingSummary.NO:
                return red;
            default:
                return amber;
        }
    }, [summaryValue]);

    const collateralSummaryIconToShow = useMemo(() => {
        switch (collateralSummaryValue) {
            case CollateralisationMethodSummary.YES:
                return green;
            case CollateralisationMethodSummary.NO:
                return red;
            default:
                return amber;
        }
    }, [collateralSummaryValue]);

    const conclusions = useMemo(() => (
        <div className={styles.conclusions}>
            <div className={styles.conclusionWrapper}>
                <div className={styles.conclusion}>Close out netting:</div>
                <div className={styles.conclusionValue} style={{ color: closeOutSummaryColour }}>{summaryValue}</div>
            </div>
            {agreementType === AgreementType.GMRA &&
                <div className={styles.conclusionWrapper}>
                    <div className={styles.conclusion}>Collateralisation method enforceability:</div>
                    <div className={styles.conclusionValue} style={{ color: collateralSummaryIconToShow }}>{collateralSummaryValue}</div>
                </div>
            }
        </div>
    ), [agreementType, closeOutSummaryColour, collateralSummaryIconToShow, collateralSummaryValue, summaryValue]);

    return <NettingEngineJurisdictionResultsSection label='Summary' children={conclusions} />;
};
